import * as React from "react";
import type { Student, TenantVerticalLiteral } from "@eduviaos/shared";
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@eduviaos/ui-kit";
import {
  ATTENDANCE_STATUS_LABELS,
  type AttendanceStatus,
  useMarkAttendance,
} from "@/lib/attendance/api";

interface MarkAttendanceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  learners: Student[];
  dateKey: string;
  vertical: TenantVerticalLiteral | undefined;
  personLabel: string;
}

export function MarkAttendanceSheet({
  open,
  onOpenChange,
  learners,
  dateKey,
  vertical,
  personLabel,
}: MarkAttendanceSheetProps) {
  const mark = useMarkAttendance(vertical);
  const [studentId, setStudentId] = React.useState("");
  const [status, setStatus] = React.useState<AttendanceStatus>("present");
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setStudentId("");
      setStatus("present");
      setNote("");
    }
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!studentId) return;
    await mark.mutateAsync({
      student_id: studentId,
      status,
      date_key: dateKey,
      note: note.trim() || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <form onSubmit={(e) => void handleSubmit(e)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>Mark attendance</SheetTitle>
            <SheetDescription>
              Record check-in for {dateKey}. Learners who already have a row for this day will
              update in place.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="attendance-learner">{personLabel}</Label>
              <Select value={studentId} onValueChange={setStudentId} required>
                <SelectTrigger id="attendance-learner">
                  <SelectValue placeholder={`Select ${personLabel.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {learners.map((learner) => (
                    <SelectItem key={learner.id} value={learner.id}>
                      {learner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="attendance-status">Status</Label>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value as AttendanceStatus)}
              >
                <SelectTrigger id="attendance-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ATTENDANCE_STATUS_LABELS) as AttendanceStatus[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {ATTENDANCE_STATUS_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="attendance-note">Note (optional)</Label>
              <Textarea
                id="attendance-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Shift, seat zone, or reason"
              />
            </div>
          </div>

          <div className="mt-auto flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!studentId || mark.isPending}>
              {mark.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
